// src/components/EquivalentFractionsView.tsx
/**
 * Renders a stacked set of number lines for equivalent fractions mode.
 * Each row locks ticks to a specific denominator so aligned fractions are visible.
 */

import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../types/redux';
import { NumberLine } from './NumberLine';
import {
  addDenominator,
  removeDenominator,
  resetActiveDenominators,
  setLabelFormat,
} from '../store/slices/equivalentFractionsSlice';
import { computeVisibleDomain } from '../lib/scale';

interface EquivalentFractionsViewProps {
  width: number;
  height: number;
  /**
   * Optional layout size representing the visible viewport width for controls.
   * The actual number line can stay larger (for stable scaling) while controls
   * align with the clipped viewport width.
   */
  layoutWidth?: number;
  /**
   * Optional layout size representing the visible viewport height for controls.
   * Helps avoid the top bar getting clipped when the canvas is vertically
   * centered in a smaller viewport (e.g., fullscreen with a tall max canvas).
   */
  layoutHeight?: number;
  /**
   * Whether the canvas is in fullscreen mode. Used to reserve a predictable
   * bar for the controls instead of letting them overlap the canvas.
   */
  isFullscreen?: boolean;
  className?: string;
  exportRef?: React.RefObject<HTMLDivElement>;
}

export const EquivalentFractionsView: React.FC<EquivalentFractionsViewProps> = ({
  width,
  height,
  layoutWidth,
  layoutHeight,
  isFullscreen = false,
  className = '',
  exportRef,
}) => {
  const dispatch = useDispatch();
  const { denominators, activeDenominators, integerLabelsOnly, labelFormat } = useSelector(
    (state: RootState) => state.equivalentFractions
  );
  const theme = useSelector((state: RootState) => state.preferences.theme);
  const viewport = useSelector((state: RootState) => state.viewport);
  const showIntegerLine = useSelector(
    (state: RootState) => state.ui.showEquivalentIntegerLine !== false
  );

  const MAX_DENOMINATORS = 12;
  const [customDenominator, setCustomDenominator] = useState('');
  const [presetDenominator, setPresetDenominator] = useState('');
  const [inputError, setInputError] = useState<string | null>(null);

  const presetOptions = useMemo(() => {
    const suggestions = [2, 3, 4, 5, 6, 8, 10, 12, 16, ...denominators];
    return Array.from(new Set(suggestions)).sort((a, b) => a - b);
  }, [denominators]);

  const rows = useMemo(
    () => {
      const source = activeDenominators.length > 0 ? activeDenominators : denominators;
      return [...source].sort((a, b) => a - b);
    },
    [activeDenominators, denominators]
  );

  // Least common multiple of denominators to align equivalent values
  const lcmDenominator = useMemo(() => {
    const gcd = (a: number, b: number) => b === 0 ? a : gcd(b, a % b);
    const lcm = (a: number, b: number) => Math.abs(a * b) / gcd(a, b || 1);
    return rows.reduce((acc, d) => lcm(acc, d), 1);
  }, [rows]);

  const rowHeight = useMemo(() => {
    const minHeight = 45;
    const targetRows = Math.min(rows.length || 1, 5);
    const available = Math.max(height * 0.9 - 6, minHeight * targetRows);
    const suggested = Math.floor(available / targetRows);
    return Math.max(minHeight, Math.min(suggested, 140));
  }, [height, rows.length]);

  const handleAddDenominator = () => {
    const parsedCustom = parseInt(customDenominator, 10);
    const customValue = Number.isFinite(parsedCustom) ? parsedCustom : null;
    const presetValue = presetDenominator ? parseInt(presetDenominator, 10) : null;
    const value = customValue && customValue > 0 ? customValue : presetValue;

    if (!value || value <= 0 || !Number.isFinite(value)) {
      setInputError('Enter a positive integer denominator.');
      return;
    }

    if (rows.includes(value)) {
      setInputError('That denominator is already shown.');
      return;
    }

    if (rows.length >= MAX_DENOMINATORS) {
      setInputError(`Limit of ${MAX_DENOMINATORS} lines reached.`);
      return;
    }

    setInputError(null);
    dispatch(addDenominator(value));
    setCustomDenominator('');
    setPresetDenominator('');
  };

  // Shared domain and alignment positions (major ticks) for vertical dotted guides
  const isVerticalOrientation = viewport.orientation === 'vertical';
  const isHorizontal = !isVerticalOrientation;
  const lineLength = isHorizontal ? width : height;
  const activeOffset = isHorizontal ? viewport.offsetX : (viewport.offsetY ?? viewport.offsetX);
  const domain = useMemo(
    () => computeVisibleDomain(activeOffset, viewport.scale, lineLength),
    [activeOffset, viewport.scale, lineLength]
  );

  const alignmentPositions = useMemo(() => {
    if (!Number.isFinite(lcmDenominator) || lcmDenominator <= 0) return [];
    const range = domain.end - domain.start;
    const pxPerUnit = range > 0 ? lineLength / range : 0;
    const baseStep = 1 / lcmDenominator;
    const baseStepPx = baseStep * pxPerUnit;
    const minGuideSpacingPx = 28;
    const stride = baseStepPx > 0 ? Math.max(1, Math.floor(minGuideSpacingPx / baseStepPx)) : 1;

    const startK = Math.ceil((domain.start * lcmDenominator) / stride) * stride;
    const endK = Math.floor((domain.end * lcmDenominator) / stride) * stride;
    const maxLines = 2000;
    if ((endK - startK) / stride > maxLines) return [];

    const values: number[] = [];
    for (let k = startK; k <= endK; k += stride) {
      values.push(k / lcmDenominator);
    }
    return values;
  }, [
    domain,
    lcmDenominator,
  ]);

  const effectiveLayoutWidth = layoutWidth ?? width;
  const effectiveLayoutHeight = layoutHeight ?? height;
  const horizontalInset = Math.max(0, (width - effectiveLayoutWidth) / 2);
  // Align controls to the top of the visible canvas in all modes.
  // Avoid pushing them down by the vertical inset so they can't hide.
  const verticalInset = Math.max(0, (height - effectiveLayoutHeight) / 2);
  const controlsBarHeight = 44;
  // Offset controls below the in-app header in fullscreen while keeping windowed mode tight.
  const fullscreenHeaderOffset = 48;
  const basePaddingX = 4;
  const basePaddingY = 2;
  const controlsTopOffset = isFullscreen ? fullscreenHeaderOffset : 0;
  const controlsPaddingTop = basePaddingY + controlsTopOffset;
  const rowsTopPadding = controlsBarHeight + basePaddingY + controlsTopOffset;
  const verticalContentHeight = useMemo(() => {
    if (!isVerticalOrientation) return null;
    return Math.max(120, height - controlsPaddingTop - basePaddingY - verticalInset);
  }, [isVerticalOrientation, height, controlsPaddingTop, basePaddingY, verticalInset]);

  // Extra spacing between horizontal rows to avoid visual overlap in exports
  const rowGap = 12;

  // Compact helper height for the optional integer-only top line (horizontal only)
  const compactIntegerLineHeight = useMemo(() => {
    if (isVerticalOrientation) return null;
    const base = Math.max(28, Math.floor(rowHeight * 0.6));
    return Math.min(40, base);
  }, [isVerticalOrientation, rowHeight]);

  // Vertical orientation: compute column widths and full-height line space
  const columnWidth = useMemo(() => {
    if (!isVerticalOrientation) return null;
    const availableWidth = Math.max(
      100,
      effectiveLayoutWidth - 2 * (basePaddingX + horizontalInset)
    );
    const visibleColumns = Math.min(rows.length || 1, 5);
    const rawWidth = Math.floor(availableWidth / Math.max(1, visibleColumns));
    return Math.max(110, Math.min(220, rawWidth));
  }, [
    isVerticalOrientation,
    effectiveLayoutWidth,
    basePaddingX,
    horizontalInset,
    rows.length,
  ]);

  const verticalLineHeight = useMemo(() => {
    if (!isVerticalOrientation) return null;
    const availableHeight = Math.max(
      160,
      height - rowsTopPadding - basePaddingY - verticalInset
    );
    return availableHeight;
  }, [isVerticalOrientation, height, rowsTopPadding, basePaddingY, verticalInset]);

  const resolvedVerticalHeight = useMemo(() => {
    if (!isVerticalOrientation) return null;
    return (
      verticalLineHeight ??
      Math.max(160, height - rowsTopPadding - basePaddingY - verticalInset)
    );
  }, [isVerticalOrientation, verticalLineHeight, height, rowsTopPadding, basePaddingY, verticalInset]);

  const resolvedVerticalWidth = useMemo(() => {
    if (!isVerticalOrientation) return null;
    return (
      columnWidth ??
      Math.max(140, Math.min(280, Math.floor(width / Math.max(rows.length, 1))))
    );
  }, [isVerticalOrientation, columnWidth, width, rows.length]);

  const rootOverflowStyles = useMemo(
    () =>
      isVerticalOrientation
        ? {
            overflowX: 'hidden' as const,
            overflowY: 'hidden' as const,
            overscrollBehavior: 'contain' as const,
          }
        : {
            overflowX: 'hidden' as const,
            overflowY: 'auto' as const,
          },
    [isVerticalOrientation]
  );

  const railOverflowStyles = useMemo(
    () =>
      isVerticalOrientation
        ? {
            overflowX: 'auto' as const,
            overflowY: 'hidden' as const,
            overscrollBehaviorX: 'contain' as const,
          }
        : {
            overflowY: 'visible' as const,
          },
    [isVerticalOrientation]
  );

  return (
    <div
      ref={exportRef}
      className={`flex flex-col h-full w-full ${className}`.trim()}
      style={{
        ...rootOverflowStyles,
        gap: '1px',
        paddingTop: controlsPaddingTop,
        paddingBottom: basePaddingY + verticalInset,
        paddingLeft: basePaddingX + horizontalInset,
        paddingRight: basePaddingX + horizontalInset,
        // Ensure keyboard focus / scroll aligns the controls to the viewport start.
        scrollPaddingTop: controlsPaddingTop,
      }}
      data-equivalent-fractions-container
    >
      <div
        className={`sticky z-30 flex flex-col gap-1 px-2 py-1 w-full pointer-events-none ${
          theme === 'dark' ? 'bg-gray-900' : 'bg-white'
        }`}
        style={{
          minWidth: 0,
          top: controlsTopOffset,
          left: 0,
          right: 0,
          minHeight: controlsBarHeight,
        }}
      >
        <div
          className="pointer-events-auto w-full overflow-x-auto"
          style={{
            scrollbarGutter: 'stable both-edges',
            paddingLeft: '6px',
            paddingRight: '8px',
          }}
        >
          <div className="inline-flex flex-wrap items-center gap-2 min-w-full pr-2">
            <label className="text-xs text-gray-600 dark:text-gray-400">Add line</label>
            <select
              value={presetDenominator}
              onChange={(e) => setPresetDenominator(e.target.value)}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shrink-0"
              style={{ minWidth: '110px' }}
            >
              <option value="">Choose preset</option>
              {presetOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
            <input
              type="number"
              min={1}
              step={1}
              inputMode="numeric"
              value={customDenominator}
              onChange={(e) => setCustomDenominator(e.target.value)}
              placeholder="Custom"
              className="w-24 text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100"
            />
            <button
              type="button"
              onClick={handleAddDenominator}
              className="text-xs px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 shrink-0"
              disabled={rows.length >= MAX_DENOMINATORS}
            >
              Add line
            </button>
            <button
              type="button"
              onClick={() => dispatch(resetActiveDenominators())}
              className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 shrink-0"
            >
              Reset to defaults
            </button>
            <div className="flex items-center gap-2 ml-auto shrink-0">
              <label className="text-xs text-gray-600 dark:text-gray-400">Labels</label>
              <select
                value={labelFormat}
                onChange={(e) => dispatch(setLabelFormat(e.target.value as 'fraction' | 'decimal'))}
                className="text-xs px-2 py-1 rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 shrink-0"
                style={{ minWidth: '110px' }}
              >
                <option value="fraction">Fractions</option>
                <option value="decimal">Decimals</option>
              </select>
            </div>
          </div>
        </div>
        {inputError && (
          <div className="text-xs text-red-500 pointer-events-auto px-1">{inputError}</div>
        )}
      </div>
      <div
        className={`relative ${isVerticalOrientation ? 'overflow-x-auto' : ''}`}
        style={{
          minHeight: isVerticalOrientation
            ? (verticalLineHeight ?? Math.max(160, height - rowsTopPadding))
            : rowHeight * rows.length +
              rowGap * Math.max(rows.length - 1, 0) +
              (showIntegerLine ? (compactIntegerLineHeight ?? rowHeight) + rowGap : 0),
          paddingTop: rowsTopPadding,
          ...railOverflowStyles,
        }}
        data-equivalent-fractions-rail
      >
        {isVerticalOrientation ? (
          <div
            className="flex gap-0.5 pr-1"
            style={{
              minHeight: resolvedVerticalHeight ?? Math.max(160, height - rowsTopPadding),
              paddingBottom: basePaddingY + verticalInset,
            }}
          >
            {showIntegerLine && (
              <div
                className={`relative flex flex-col ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
                style={{
                  padding: '2px 8px 8px 2px',
                  width: resolvedVerticalWidth ?? Math.max(140, Math.min(280, Math.floor(width / Math.max(rows.length, 1)))),
                  minWidth: resolvedVerticalWidth ?? Math.max(140, Math.min(280, Math.floor(width / Math.max(rows.length, 1)))),
                  height: resolvedVerticalHeight ?? Math.max(160, height - rowsTopPadding),
                }}
                aria-label="Integer line showing whole numbers"
              >
                <div style={{ overflow: 'visible', height: '100%' }}>
                  <NumberLine
                    width={resolvedVerticalWidth ?? Math.max(140, Math.min(280, Math.floor(width / Math.max(rows.length, 1))))}
                    height={resolvedVerticalHeight ?? Math.max(160, height - rowsTopPadding)}
                    className="w-full h-full"
                    labelPlacement="above"
                    equivalentFractions={{ denominator: 1, integerLabelsOnly: true, labelFormat: 'decimal' }}
                    alignmentGuides={alignmentPositions}
                  />
                </div>
              </div>
            )}
            {rows.map((denominator, index) => {
              const lineHeight = resolvedVerticalHeight ?? Math.max(160, height - rowsTopPadding);
              const lineWidth =
                resolvedVerticalWidth ??
                Math.max(140, Math.min(280, Math.floor(width / Math.max(rows.length, 1))));
              return (
                <div
                  key={`${denominator}-${index}`}
                  className={`relative flex flex-col ${
                    theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                  }`}
                  style={{
                    padding: '2px 8px 8px 2px',
                    width: lineWidth,
                    minWidth: lineWidth,
                    height: lineHeight,
                  }}
                  aria-label={`Equivalent fraction line for denominator ${denominator}`}
                >
                  <div className="overflow-hidden h-full">
                    <NumberLine
                      width={lineWidth}
                      height={lineHeight}
                      className="w-full h-full"
                      equivalentFractions={{ denominator, integerLabelsOnly, labelFormat }}
                      alignmentGuides={alignmentPositions}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => dispatch(removeDenominator(denominator))}
                    className="absolute left-2 top-2 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm"
                    aria-label={`Remove line with denominator ${denominator}`}
                    title="Remove line"
                  >
                    ×
                  </button>
                </div>
              );
            })}
          </div>
        ) : (
          <>
            {showIntegerLine && (
              <div
                className={`relative flex flex-col ${theme === 'dark' ? 'bg-gray-900' : 'bg-white'}`}
                style={{ padding: '2px 10px 0 2px' }}
                aria-label="Integer line showing whole numbers"
              >
                <div style={{ overflow: 'visible' }}>
                  <NumberLine
                    width={width}
                    height={compactIntegerLineHeight ?? rowHeight}
                    className="w-full h-full"
                    labelPlacement="above"
                    equivalentFractions={{ denominator: 1, integerLabelsOnly: true, labelFormat: 'decimal' }}
                    alignmentGuides={alignmentPositions}
                  />
                </div>
              </div>
            )}
            {rows.map((denominator, index) => (
              <div
                key={`${denominator}-${index}`}
                className={`relative flex flex-col ${
                  theme === 'dark' ? 'bg-gray-900' : 'bg-white'
                }`}
                style={{
                  padding: '2px 12px 2px 2px',
                  marginBottom: rowGap,
                }}
                aria-label={`Equivalent fraction line for denominator ${denominator}`}
              >
                <div className="overflow-hidden">
                  <NumberLine
                    width={width}
                    height={rowHeight}
                    className="w-full h-full"
                    equivalentFractions={{ denominator, integerLabelsOnly, labelFormat }}
                    alignmentGuides={alignmentPositions}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => dispatch(removeDenominator(denominator))}
                  className="absolute left-2 top-2 px-2 py-0.5 rounded-full border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 shadow-sm"
                  aria-label={`Remove line with denominator ${denominator}`}
                  title="Remove line"
                >
                  ×
                </button>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default EquivalentFractionsView;
